import { makeAutoObservable } from 'mobx';
import type { RootStore } from './root-store';
import { LayoutEnum, ThemeEnum } from '../../defs/api';

export type ThemeStoreHydration = {
    layout: LayoutEnum;

    theme: ThemeEnum;

    mobileNavigationDisplay: boolean;

    mobileAdminNavigationDisplay: boolean;

    mobilePortfolioNavigationDisplay: boolean;

    showBar: boolean;

    showCriticalNotification: boolean;
};

export class ThemeStore {
    rootStore: RootStore;

    layout: LayoutEnum = LayoutEnum.Horizontal;

    theme: ThemeEnum = ThemeEnum.Light;

    mobileNavigationDisplay: boolean = false;

    mobileAdminNavigationDisplay: boolean = false;

    mobilePortfolioNavigationDisplay: boolean = false;

    showBar: boolean = false;

    showCriticalNotification: boolean = false;

    loading = false;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeAutoObservable(this, {
            rootStore: false,
        });
    }

    hydrate(): ThemeStoreHydration {
        return {
            theme: this.theme,
            layout: this.layout,
            mobileNavigationDisplay: this.mobileNavigationDisplay,
            mobileAdminNavigationDisplay: this.mobileAdminNavigationDisplay,
            mobilePortfolioNavigationDisplay: this.mobilePortfolioNavigationDisplay,
            showBar: this.showBar,
            showCriticalNotification: this.showCriticalNotification,
        };
    }

    rehydrate(data: ThemeStoreHydration): void {
        this.theme = data.theme;
        this.layout = data.layout;
        this.showBar = data.showBar;
        this.showCriticalNotification = data.showCriticalNotification;
    }

    init(): void {
        this.layout = this.rootStore.userStore.settings?.layout || LayoutEnum.Horizontal;
        this.theme = this.rootStore.userStore.settings?.theme || ThemeEnum.Light;
        this.mobileNavigationDisplay = false;
        this.mobileAdminNavigationDisplay = false;
        this.mobilePortfolioNavigationDisplay = false;

        this.showCriticalNotification = !!this.rootStore.criticalNotificationStore.lastCriticalNotification;

        this.showBar =
            !!this.rootStore.authStore.userProfile?.subscriptionEndDate &&
            (new Date(this.rootStore.authStore.userProfile?.subscriptionEndDate).getTime() - Date.now()) /
                (1000 * 3600 * 24) <=
                14;
    }

    startLoading(): void {
        this.loading = true;
    }

    stopLoading(): void {
        this.loading = false;
    }

    setDefaultTheme(): void {
        this.theme = ThemeEnum.Light;
    }

    async toggleTheme() {
        this.theme = this.theme === ThemeEnum.Light ? ThemeEnum.Dark : ThemeEnum.Light;
        try {
            this.startLoading();
            const res = await this.rootStore.apiClient.userController.userControllerUpdateSettings({
                updateUserSettingsDto: { theme: this.theme },
            });
            if (!res) {
                throw new Error();
            }

            const themeColor = this.theme === ThemeEnum.Dark ? '#1A1A29' : '#ffffff';
            const metaThemeColor = document.querySelector('meta[name="theme-color"]');
            if (metaThemeColor) {
                metaThemeColor.setAttribute('content', themeColor);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.stopLoading();
        }
    }

    async toggleLayout() {
        this.layout = this.layout === LayoutEnum.Horizontal ? LayoutEnum.Vertical : LayoutEnum.Horizontal;
        try {
            this.startLoading();
            const res = await this.rootStore.apiClient.userController.userControllerUpdateSettings({
                updateUserSettingsDto: { layout: this.layout },
            });
            if (!res) {
                throw new Error();
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.stopLoading();
        }
    }

    toggleMobileNavigationDisplay() {
        this.mobileNavigationDisplay = this.mobileNavigationDisplay !== true;
    }

    toggleMobileAdminNavigationDisplay() {
        this.mobileAdminNavigationDisplay = this.mobileAdminNavigationDisplay !== true;
    }

    toggleMobilePortfolioNavigationDisplay() {
        this.mobilePortfolioNavigationDisplay = this.mobilePortfolioNavigationDisplay !== true;
    }

    hideMobileNavigation() {
        this.mobileNavigationDisplay = false;
    }

    hideMobileAdminNavigation() {
        this.mobileAdminNavigationDisplay = false;
    }

    hideMobilePortfolioNavigation() {
        this.mobilePortfolioNavigationDisplay = false;
    }

    toggleShowBar() {
        this.showBar = this.showBar !== true;
    }

    get showHeaderBar(): boolean {
        return this.showBar || this.showCriticalNotification;
    }
}
